import React, { useEffect, useState } from "react";
import { Button, Form, Image, Table } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import { CustomModal } from "../../components";
import { toast } from "react-toastify";
import { Button as CustomBtn } from "../../ui";

const AdvertiseManagement = () => {
  const [advertisersData, setAdvertisersData] = useState({ advertisers: [] });
  const [isLoading, setIsLoading] = useState(true);
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [saveButton, setSaveButton] = useState(false);

  const secretKey = process.env.REACT_APP_SECRET_KEY || "";
  const baseUrl = process.env.REACT_APP_SERVER_URL;
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [selectedAdvertiser, setSelectedAdvertiser] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const today = new Date();
  const startDate = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const endDate = new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000);

  const [currentAdvertiser, setCurrentAdvertiser] = useState({
    businessLogo: null,
    bannerAdFile: null,
    schoolName: "",
    refName: "",
    businessName: "",
    startDate: startDate.toISOString().slice(0, 10),
    endDate: endDate.toISOString().slice(0, 10),
    url: "",
    sportName: "",
    adminEmail: "",
    adminPhone: "",
    adminName: "",
    notes: "",
    raffleId: "",
    billingId: "",
    billingNotes: "",
    type: "",
    is_active: false,
  });

  const [schoolRequiredError, setSchoolRequiredError] = useState(false);
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [sports, setSports] = useState([]);
  const [selectedSport, setSelectedSport] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [type, setType] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [universitiesData, setUniversitiesData] = useState([]);

  // Fetch all advertisers
  const fetchAdvertisers = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("token"))?.token || "";
      const response = await axios.get(`${baseUrl}/api/advertisers/get-all`, {
        headers: {
          "x-auth-token": token,
          "secret-key": secretKey,
        },
      });
      setAdvertisersData(response.data);
    } catch (error) {
      toast.error("Failed to fetch advertisers");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch universities
  const fetchUniversities = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("token"))?.token || "";
      const response = await axios.get(`${baseUrl}/api/university/get-all`, {
        headers: {
          "x-auth-token": token,
          "secret-key": secretKey,
        },
      });
      setUniversitiesData(response.data.university);
      setSchools(
        response.data.university.map((school) => ({
          value: school.name,
          label: school.name,
        })) || []
      );
    } catch (error) {
      toast.error("Failed to fetch universities");
    }
  };

  // Fetch sponsors
  const fetchSponsors = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("token"))?.token || "";
      const response = await axios.get(
        "https://sch-ws.com/api/raffles/get-all",
        {
          headers: {
            "x-auth-token": token,
            "secret-key": secretKey,
          },
        }
      );
      const sponsorOptions = response.data.raffles.map((sponsor) => ({
        value: sponsor._id,
        label: sponsor.sponsorName,
      }));
      setSponsors(sponsorOptions);
    } catch (error) {
      toast.error("Failed to fetch sponsors");
    }
  };

  // Fetch sports
  const fetchSports = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("token"))?.token || "";
      const response = await axios.get(
        "https://sch-ws.com/api/sports/get-all",
        {
          headers: {
            "x-auth-token": token,
            "secret-key": secretKey,
          },
        }
      );
      const sportOptions = response.data.sports.map((sport) => ({
        value: sport._id,
        label: sport.name,
      }));
      setSports(sportOptions);
    } catch (error) {
      toast.error("Failed to fetch sports");
    }
  };

  useEffect(() => {
    fetchAdvertisers();
    fetchUniversities();
    fetchSponsors();
    fetchSports();
  }, [baseUrl, secretKey]);

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];

    if (file) {
      setCurrentAdvertiser((prev) => ({ ...prev, [name]: file }));

      const reader = new FileReader();
      reader.onloadend = () => {
        if (name === "businessLogo") {
          setLogoPreview(reader.result);
        } else if (name === "bannerAdFile") {
          setBannerPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const openModal = (advertiser = null) => {
    if (advertiser) {
      setCurrentAdvertiser({
        ...advertiser,
        startDate: advertiser.startDate
          ? new Date(advertiser.startDate).toISOString().split("T")[0]
          : "",
        endDate: advertiser.endDate
          ? new Date(advertiser.endDate).toISOString().split("T")[0]
          : "",
      });
      setIsEditing(true);
      setSelectedSchool(
        advertiser.schoolName
          ? { value: advertiser.schoolName, label: advertiser.schoolName }
          : null
      );
      setType(advertiser.type);
      setLogoPreview(`${baseUrl}/${advertiser.businessLogo}`);
      setBannerPreview(`${baseUrl}/${advertiser.bannerAdFile}`);

      // Set selected sponsor if refName exists
      if (advertiser.refName) {
        const sponsor = sponsors.find((s) => s.value === advertiser.refName);
        setSelectedSponsor(sponsor);
      }

      // Set selected sport if sportName exists
      if (advertiser.sportName) {
        const sport = sports.find((s) => s.value === advertiser.sportName);
        setSelectedSport(sport);
      }
    } else {
      setCurrentAdvertiser({
        businessLogo: null,
        bannerAdFile: null,
        schoolName: "",
        refName: "",
        businessName: "",
        startDate: startDate.toISOString().slice(0, 10),
        endDate: endDate.toISOString().slice(0, 10),
        url: "",
        sportName: "",
        adminEmail: "",
        adminPhone: "",
        adminName: "",
        notes: "",
        raffleId: "",
        billingId: "",
        billingNotes: "",
        type: "",
        is_active: false,
      });
      setIsEditing(false);
      setSelectedSchool(null);
      setSelectedSponsor(null);
      setSelectedSport(null);
      setLogoPreview(null);
      setBannerPreview(null);
    }
    setModalOpen(true);
  };

  const handleSponsorChange = (selectedOption) => {
    setSelectedSponsor(selectedOption);
    setCurrentAdvertiser((prev) => ({
      ...prev,
      refName: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleSportChange = (selectedOption) => {
    setSelectedSport(selectedOption);
    setCurrentAdvertiser((prev) => ({
      ...prev,
      sportName: selectedOption ? selectedOption.value : "",
    }));
  };

  const openViewModal = async (advertiserId) => {
    try {
      const token = JSON.parse(localStorage.getItem("token"))?.token || "";
      const response = await axios.get(
        `${baseUrl}/api/advertisers/get-single/${advertiserId}`,
        {
          headers: {
            "x-auth-token": token,
            "secret-key": secretKey,
          },
        }
      );
      setSelectedAdvertiser(response.data.advertiser);
      setViewModalOpen(true);
    } catch (error) {
      toast.error("Failed to fetch advertiser details");
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedAdvertiser(null);
  };

  const closeModal = () => {
    setModalOpen(false);
    setIsEditing(false);
    setLogoPreview(null);
    setBannerPreview(null);
    setSchoolRequiredError(false);
    setSelectedSchool(null);
    setSelectedSponsor(null);
    setSelectedSport(null);
    setCurrentAdvertiser({
      businessLogo: null,
      bannerAdFile: null,
      schoolName: "",
      refName: "",
      businessName: "",
      startDate: "",
      endDate: "",
      url: "",
      sportName: "",
      adminEmail: "",
      adminPhone: "",
      adminName: "",
      notes: "",
      raffleId: "",
      billingId: "",
      billingNotes: "",
      type: "",
      is_active: false,
    });
  };

  const handleCreateOrUpdate = async (e) => {
    setSaveButton(true);
    e.preventDefault();
    setErrors({});

    if (!selectedSchool) {
      setSchoolRequiredError(true);
      setSaveButton(false);
      return;
    }
    setSchoolRequiredError(false);

    const formData = new FormData();
    formData.append("schoolName", selectedSchool.value);

    const fields = [
      "refName",
      "businessName",
      "startDate",
      "endDate",
      "url",
      "sportName",
      "adminEmail",
      "adminPhone",
      "adminName",
      "notes",
      "raffleId",
      "billingId",
      "billingNotes",
      "type",
      "is_active",
    ];

    fields.forEach((field) => {
      if (
        currentAdvertiser[field] !== undefined &&
        currentAdvertiser[field] !== null
      ) {
        formData.append(field, currentAdvertiser[field]);
      }
    });

    if (currentAdvertiser?.businessLogo) {
      formData.append("businessLogo", currentAdvertiser.businessLogo);
    }
    if (currentAdvertiser?.bannerAdFile) {
      formData.append("bannerAdFile", currentAdvertiser.bannerAdFile);
    }

    const token = JSON.parse(localStorage.getItem("token"))?.token || "";

    try {
      if (isEditing) {
        await axios.put(
          `${baseUrl}/api/advertisers/update/${currentAdvertiser._id}`,
          formData,
          {
            headers: {
              "x-auth-token": token,
              "secret-key": secretKey,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        toast.success("Advertiser updated successfully");
      } else {
        await axios.post(`${baseUrl}/api/advertisers/create`, formData, {
          headers: {
            "x-auth-token": token,
            "secret-key": secretKey,
            "Content-Type": "multipart/form-data",
          },
        });
        toast.success("Advertiser created successfully");
      }

      closeModal();
      fetchAdvertisers();
    } catch (error) {
      if (error.response?.data?.error) {
        const errorMessages = error.response.data.error.replace(
          "Advertiser validation failed: ",
          ""
        );
        const errorFields = errorMessages
          .split(", ")
          .map((field) => field.trim());

        const newErrors = {};
        errorFields.forEach((field) => {
          if (field.includes(": ")) {
            const [key, message] = field.split(": ");
            newErrors[key.trim()] = message
              .replace(/`/g, "")
              .replace("Path ", "")
              .trim();
          } else {
            newErrors[field.trim()] = "An unknown error occurred.";
          }
        });
        setErrors(newErrors);
      } else {
        toast.error(error.response?.data?.message || "An error occurred");
      }
    } finally {
      setSaveButton(false);
    }
  };

  const handleSchoolChange = (newValue) => {
    setSelectedSchool(newValue);
  };

  const handleEdit = (event, advertiser) => {
    event.stopPropagation();
    openModal(advertiser);
  };

  const handleDelete = async (event, id) => {
    event.stopPropagation();
    try {
      const token = JSON.parse(localStorage.getItem("token"))?.token || "";
      await axios.delete(`${baseUrl}/api/advertisers/delete/${id}`, {
        headers: {
          "x-auth-token": token,
          "secret-key": secretKey,
        },
      });
      toast.success("Advertiser deleted successfully");
      fetchAdvertisers();
    } catch (error) {
      toast.error("Failed to delete advertiser");
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setCurrentAdvertiser((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setCurrentAdvertiser((prev) => ({ ...prev, [name]: value }));
    }
  };

  const sortedAdvertisers = [...advertisersData.advertisers].sort((a, b) => {
    const updatedAtA = new Date(a.updatedAt);
    const updatedAtB = new Date(b.updatedAt);
    if (updatedAtA > updatedAtB) return -1;
    if (updatedAtA < updatedAtB) return 1;
    return 0;
  });

  const paginatedUsers = sortedAdvertisers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(sortedAdvertisers.length / itemsPerPage);

  if (isLoading) return <p>Loading...</p>;

  return (
    <div className="container">
      <div className="main_containerAdmin">
        <h3 className="mb-4">Advertise Management</h3>
        <Button
          variant="danger"
          className="mb-3"
          onClick={() => openModal(null)}
        >
          Create Advertiser
        </Button>
      </div>

      <Table striped bordered hover responsive className="advertisers-table">
        <thead>
          <tr>
            <th>School Name</th>
            <th>Sponsor</th>
            <th>Business Name</th>
            <th>Business Logo</th>
            <th>Banner Ad File</th>
            <th>Type</th>
            <th>Is Active</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>URL</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedUsers.map((advertiser) => {
            const sponsor = sponsors.find(
              (s) => s.value === advertiser.refName
            );
            const sport = sports.find((s) => s.value === advertiser.sportName);
            return (
              <tr
                key={advertiser._id}
                onClick={() => openViewModal(advertiser._id)}
                style={{ cursor: "pointer" }}
              >
                <td>{advertiser.schoolName}</td>
                <td>{sponsor ? sponsor.label : "N/A"}</td>
                <td>{advertiser.businessName}</td>
                <td>
                  <Image
                    style={{ objectFit: "cover" }}
                    src={`${baseUrl}/${advertiser.businessLogo}`}
                    alt="Business Logo"
                    width="50"
                    height="50"
                    rounded
                  />
                </td>
                <td>
                  <Image
                    style={{ objectFit: "cover" }}
                    src={`${baseUrl}/${advertiser.bannerAdFile}`}
                    alt="Banner Ad"
                    width="50"
                    height="50"
                    rounded
                  />
                </td>
                <th>{advertiser?.type}</th>
                <td>{advertiser?.is_active ? "Yes" : "No"}</td>
                <td>
                  {new Date(advertiser.startDate).toLocaleDateString("en-GB")}
                </td>
                <td>
                  {new Date(advertiser.endDate).toLocaleDateString("en-GB")}
                </td>
                <td>
                  <a
                    href={advertiser.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Link
                  </a>
                </td>
                <td>
                  <div className="admin_Actions_gap">
                    <img
                      className="Update-delete-icon"
                      onClick={(event) => handleEdit(event, advertiser)}
                      width="30"
                      height="30"
                      src="https://img.icons8.com/ios-glyphs/30/create-new.png"
                      alt="create-new"
                    />
                    <img
                      className="Update-delete-icon"
                      onClick={(event) => handleDelete(event, advertiser._id)}
                      width="30"
                      height="30"
                      src="https://img.icons8.com/ios-glyphs/30/filled-trash.png"
                      alt="filled-trash"
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <div className="d-flex justify-content-center mt-3">
        {Array.from({ length: totalPages }, (_, index) => (
          <Button
            key={index}
            variant={currentPage === index + 1 ? "primary" : "outline-primary"}
            onClick={() => setCurrentPage(index + 1)}
            className="mx-1"
          >
            {index + 1}
          </Button>
        ))}
      </div>

      {isViewModalOpen && selectedAdvertiser && (
        <CustomModal isOpen={isViewModalOpen} onClose={closeViewModal}>
          <div className="advertisement_CustomModal">
            <h3 className="advertisement_CustomModal_title">
              Advertisement Details
            </h3>
            <h4 className="advertisement_CustomModal_businesname">
              {selectedAdvertiser.businessName}
            </h4>
            <div className="advertisement_CustomModal_Cont">
              <p>
                <strong>Sponsor:</strong>{" "}
                {sponsors.find((s) => s.value === selectedAdvertiser.refName)
                  ?.label || "N/A"}
              </p>
              <p>
                <strong>School Name:</strong> {selectedAdvertiser.schoolName}
              </p>
              <p>
                <strong>Sport:</strong>{" "}
                {sports.find((s) => s.value === selectedAdvertiser.sportName)
                  ?.label || "N/A"}
              </p>
              <p>
                <strong>Type:</strong> {selectedAdvertiser.type}
              </p>
              <p>
                <strong>Start Date:</strong>{" "}
                {new Date(selectedAdvertiser.startDate).toLocaleDateString(
                  "en-GB"
                )}
              </p>
              <p>
                <strong>End Date:</strong>{" "}
                {new Date(selectedAdvertiser.endDate).toLocaleDateString(
                  "en-GB"
                )}
              </p>
              <p>
                <strong>URL:</strong>{" "}
                <a
                  href={selectedAdvertiser.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {selectedAdvertiser.url}
                </a>
              </p>
            </div>
            <div className="advertisement_CustomModal_Cont2">
              <p className="advertisement_CustomModal_Cont2_businesLogo">
                Business Logo
              </p>
              <Image
                src={`${baseUrl}/${selectedAdvertiser.businessLogo}`}
                alt="Business Logo"
                width="120"
                height="120"
                style={{
                  objectFit: "cover",
                  marginBottom: "20px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                }}
              />
              <p className="advertisement_CustomModal_Cont2">Banner Ad File</p>
              <Image
                src={`${baseUrl}/${selectedAdvertiser.bannerAdFile}`}
                alt="Banner Ad"
                width="200"
                height="120"
                style={{
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                }}
              />
            </div>
          </div>
        </CustomModal>
      )}

      <CustomModal isOpen={isModalOpen} onClose={closeModal}>
        <div className="admin_CreateModal">
          <h3 className="advertisement_CreateModal_title">
            {isEditing ? "Edit Advertiser" : "Create Advertiser"}
          </h3>
          <Form
            onSubmit={handleCreateOrUpdate}
            className="admin_CreateModal_submit"
          >
            <Form.Group>
              <Form.Select
                className="advertisement_CreateModal_form_Type"
                placeholder="Type"
                name="type"
                value={currentAdvertiser.type}
                onChange={handleChange}
                required
              >
                <option value="">Select Type</option>
                <option value="banner">banner</option>
                <option value="news">news</option>
                <option value="raffle">raffle</option>
              </Form.Select>
              <Form.Label className="advertisement_CreateModal_form_Label">
                Business Logo
              </Form.Label>
              {logoPreview && (
                <Image
                  src={logoPreview}
                  alt="Business Logo Preview"
                  width="50"
                  height="50"
                  rounded
                />
              )}
              <Form.Control
                type="file"
                name="businessLogo"
                onChange={handleFileChange}
              />
            </Form.Group>
            {errors.businessLogo && (
              <span className="text-danger">{errors.businessLogo}</span>
            )}
            <Form.Group>
              <Form.Label className="advertisement_CreateModal_form_Label">
                Banner Ad File
              </Form.Label>
              {bannerPreview && (
                <Image
                  src={bannerPreview}
                  alt="Banner Ad Preview"
                  width="50"
                  height="50"
                  rounded
                />
              )}
              <Form.Control
                type="file"
                name="bannerAdFile"
                onChange={handleFileChange}
              />
              {errors.bannerAdFile && (
                <span className="text-danger">{errors.bannerAdFile}</span>
              )}
            </Form.Group>
            <Form.Group>
              <Select
                options={schools}
                placeholder="Select School"
                value={selectedSchool}
                onChange={handleSchoolChange}
                styles={{
                  container: (base) => ({ ...base, borderRadius: "5px" }),
                }}
              />
              {schoolRequiredError && (
                <p className="advertisement_CreateModal_form_error">
                  School Name is required
                </p>
              )}
            </Form.Group>

            <Form.Group>
              <Form.Label>Sponsor</Form.Label>
              <Select
                options={sponsors}
                placeholder="Select Sponsor"
                value={selectedSponsor}
                onChange={handleSponsorChange}
                styles={{
                  container: (base) => ({ ...base, borderRadius: "5px" }),
                }}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Sport</Form.Label>
              <Select
                options={sports}
                placeholder="Select Sport"
                value={selectedSport}
                onChange={handleSportChange}
                styles={{
                  container: (base) => ({ ...base, borderRadius: "5px" }),
                }}
              />
            </Form.Group>

            <Form.Control
              placeholder="Business Name"
              name="businessName"
              value={currentAdvertiser.businessName}
              onChange={handleChange}
            />
            {errors.businessName && (
              <span className="text-danger">{errors.businessName}</span>
            )}
            <Form.Control
              placeholder="Start Date"
              type="date"
              name="startDate"
              value={
                currentAdvertiser.startDate ||
                startDate.toISOString().slice(0, 10)
              }
              onChange={handleChange}
            />
            <Form.Control
              placeholder="End Date"
              type="date"
              name="endDate"
              value={
                currentAdvertiser.endDate || endDate.toISOString().slice(0, 10)
              }
              onChange={handleChange}
            />
            <Form.Control
              placeholder="URL"
              name="url"
              value={currentAdvertiser.url}
              onChange={handleChange}
            />
            {errors.url && <span className="text-danger">{errors.url}</span>}
            <Form.Control
              placeholder="Admin Email"
              name="adminEmail"
              value={currentAdvertiser.adminEmail}
              onChange={handleChange}
            />
            <Form.Control
              placeholder="Admin Phone"
              name="adminPhone"
              value={currentAdvertiser.adminPhone}
              onChange={handleChange}
            />
            <Form.Control
              placeholder="Admin Name"
              name="adminName"
              value={currentAdvertiser.adminName}
              onChange={handleChange}
            />
            <Form.Control
              placeholder="Notes"
              name="notes"
              value={currentAdvertiser.notes}
              onChange={handleChange}
            />
            <Form.Control
              placeholder="Sponsor Sport ID"
              name="raffleId"
              value={currentAdvertiser.raffleId}
              onChange={handleChange}
            />
            <Form.Control
              placeholder="Billing Id"
              name="billingId"
              value={currentAdvertiser.billingId}
              onChange={handleChange}
            />
            <Form.Control
              placeholder="Billing Notes"
              name="billingNotes"
              value={currentAdvertiser.billingNotes}
              onChange={handleChange}
            />
            <Form.Select
              placeholder="Is Active"
              name="is_active"
              value={currentAdvertiser.is_active}
              onChange={handleChange}
              required
            >
              <option value="">Select Is Active</option>
              <option value="true">True</option>
              <option value="false">false</option>
            </Form.Select>
            <div className="advertisement_CreateModal_form_Submit">
              <CustomBtn
                loading={saveButton}
                type="submit"
                variant="primary"
                style={{
                  marginTop: "15px",
                  marginBottom: "20px",
                  textAlign: "center",
                }}
              >
                {isEditing ? "Update" : "Create"}
              </CustomBtn>
            </div>
          </Form>
        </div>
      </CustomModal>
    </div>
  );
};

export default AdvertiseManagement;
